import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { Observable, catchError, map, of, switchMap, tap } from "rxjs";

import { environment } from "../../../environments/environment";
import { ReferralService } from "../http/referral/referral.service";
import { SpineAuthorizationService, type SpineTenant } from "../surface/spine-authorization.service";
import { APP_LANGUAGES, DEFAULT_LANGUAGE } from "./i18n.tokens";

/** Where the choice lives inside the tenant's legacy settings document. */
const SETTINGS_PATH = "configuration.languages";

export interface TenantLanguages {
  /** Codes the tenant offers. Empty means "every language the portal ships". */
  readonly enabled: readonly string[];
  /** The code a new session starts in, when the reader has expressed no preference. */
  readonly fallback: string;
}

/** Every language before a tenant narrows it — the answer while nothing is known. */
export const ALL_LANGUAGES: TenantLanguages = {
  enabled: APP_LANGUAGES.map((l) => l.code),
  fallback: DEFAULT_LANGUAGE
};

function readSessionAccountId(): string | null {
  try {
    const raw = localStorage.getItem("currentUser");
    const parsed = raw === null ? null : (JSON.parse(raw) as { account_id?: unknown });
    const id = parsed?.account_id;
    return typeof id === "string" && id !== "" ? id : null;
  } catch {
    return null;
  }
}

function at(source: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (node, key) =>
        node !== null && typeof node === "object" ? (node as Record<string, unknown>)[key] : undefined,
      source
    );
}

/**
 * Which languages this organisation offers, and which it starts in.
 *
 * **The organisation's settings live on the organisation.** The spine `tenant` document already
 * held the tenant's currency, timezone and default locale — it is the org settings object — and
 * now holds `enabled_locales` beside them. That is the source this service prefers, and the one a
 * save writes to.
 *
 * A tenant with no spine row yet still has a legacy `account_setting` document, which is where
 * this setting lived first. That is the fallback: read it, and write there when there is no spine
 * tenant to write to. The two are not kept in sync deliberately — the spine answer wins whenever
 * it exists, so a tenant migrates the moment its spine row appears, without a data migration and
 * without a reader ever seeing the older answer take precedence.
 *
 * **Fail-open is the whole safety story.** Until a read lands, and whenever one fails or the
 * tenant has expressed no choice, every shipped language is offered. Guessing "narrowed" from an
 * unloaded setting would strip a reader's own language out of the switcher for the duration of a
 * slow request — and a tenant that has never opened the screen has not chosen to offer nothing,
 * it has simply not chosen.
 */
@Injectable({ providedIn: "root" })
export class TenantLanguagesService {
  private readonly http = inject(HttpClient);
  private readonly spine = inject(SpineAuthorizationService);
  private readonly settings = inject(ReferralService);
  private readonly url = environment.apiUrl;

  private readonly loaded = signal<TenantLanguages | null>(null);
  private inFlight = false;

  /** Never null: the unrestricted set stands in until a narrower one is known. */
  readonly current = computed<TenantLanguages>(() => this.loaded() ?? ALL_LANGUAGES);

  /** True once a tenant has actually chosen, so a screen can tell "all" from "not set". */
  readonly configured = computed(() => (this.loaded()?.enabled.length ?? 0) > 0);

  /** The spine tenant a save would write to, or null when only the legacy document exists. */
  readonly tenantId = computed<string | null>(() => this.spine.tenant()?.id ?? null);

  /** Loads once per session. Repeat callers get the cached answer. */
  load(): Observable<TenantLanguages> {
    const cached = this.loaded();
    if (cached !== null || this.inFlight) {
      return of(cached ?? ALL_LANGUAGES);
    }
    this.inFlight = true;

    return this.spine.load().pipe(
      switchMap((authorization) => {
        const tenant = authorization?.tenant;
        if (tenant !== undefined) {
          return of(fromSpineTenant(tenant));
        }
        return this.readLegacy();
      }),
      tap((value) => {
        this.loaded.set(value);
        this.inFlight = false;
      }),
      catchError(() => {
        // A failed read must not narrow anything; `loaded` stays null so `current` is ALL.
        this.inFlight = false;
        return of(ALL_LANGUAGES);
      })
    );
  }

  /**
   * Stores the tenant's choice, and applies it locally so the switcher narrows immediately
   * rather than on next login.
   */
  save(value: TenantLanguages): Observable<TenantLanguages> {
    return this.write(value).pipe(
      map(() => {
        this.set(value);
        return value;
      })
    );
  }

  /** Applies a saved choice without a second read. */
  set(value: TenantLanguages): void {
    this.loaded.set(value);
    const tenant = this.spine.tenant();
    if (tenant !== null) {
      this.spine.patchTenant({ enabledLocales: [...value.enabled], defaultLocale: value.fallback });
    }
  }

  /** The legacy settings fragment a save writes, so the screen and this service agree on the shape. */
  static payload(value: TenantLanguages): Record<string, unknown> {
    return {
      configuration: { languages: { enabled: [...value.enabled], fallback: value.fallback } }
    };
  }

  private write(value: TenantLanguages): Observable<unknown> {
    const tenantId = this.tenantId();
    if (tenantId !== null) {
      return this.http.patch<unknown>(`${this.url}/tenants/${encodeURIComponent(tenantId)}`, {
        enabled_locales: [...value.enabled],
        default_locale: value.fallback
      });
    }

    const accountId = readSessionAccountId();
    if (accountId === null) {
      return of(null);
    }
    // The legacy settings endpoint takes its payload in a multipart `data` envelope.
    const form = new FormData();
    form.append("data", JSON.stringify(TenantLanguagesService.payload(value)));
    return this.settings.updateAccountSetting(accountId, form);
  }

  private readLegacy(): Observable<TenantLanguages> {
    const accountId = readSessionAccountId();
    if (accountId === null) {
      return of(ALL_LANGUAGES);
    }
    return this.http
      .get<unknown>(`${this.url}/account_settings/${encodeURIComponent(accountId)}?$mode=Company`)
      .pipe(
        map((res) => toTenantLanguages(res)),
        catchError(() => of(ALL_LANGUAGES))
      );
  }
}

/**
 * The org settings the spine already returns, read as a language choice.
 *
 * An empty `enabled_locales` is the tenant's "no restriction", which is the same answer as never
 * having chosen — so it resolves to every shipped language rather than to none.
 */
export function fromSpineTenant(tenant: Pick<SpineTenant, "enabledLocales" | "defaultLocale">): TenantLanguages {
  const known = new Set(APP_LANGUAGES.map((l) => l.code));
  const enabled = tenant.enabledLocales.filter((code) => known.has(code));
  if (enabled.length === 0) {
    return ALL_LANGUAGES;
  }
  const stated = tenant.defaultLocale;
  return { enabled, fallback: enabled.includes(stated) ? stated : enabled[0] };
}

export function toTenantLanguages(res: unknown): TenantLanguages {
  const node = at(res, SETTINGS_PATH);
  if (node === null || typeof node !== "object") {
    return ALL_LANGUAGES;
  }

  const raw = node as { enabled?: unknown; fallback?: unknown };
  const known = new Set(APP_LANGUAGES.map((l) => l.code));
  // A code the portal no longer ships is dropped rather than offered: the switcher would have
  // nothing to load for it.
  const enabled = Array.isArray(raw.enabled)
    ? raw.enabled.filter((c): c is string => typeof c === "string" && known.has(c))
    : [];

  if (enabled.length === 0) {
    return ALL_LANGUAGES;
  }

  const stated = typeof raw.fallback === "string" ? raw.fallback : DEFAULT_LANGUAGE;
  // The fallback must be one the tenant actually offers, or a new session starts in a language
  // its own switcher will not show.
  const fallback = enabled.includes(stated) ? stated : enabled[0];

  return { enabled, fallback };
}
